import React, { useRef, useState } from "react";
import { Box, Input, Switch, SelectOption, Select, Button, Flyout } from "@showitinc/showit-wc";

import UIActions from "../../../actions/UIActions";
import ColorPicker from "../../base/ColorPicker";
import FontSelectInput from "../../../components/FontSelectInput";
import AppConstants from "../../../constants/AppConstants";
import { INPUT_TYPE_NUMBER } from "../../../constants/ElementConstants";
import { initFormatter } from "../../../utils/PropertyFormatters";
import useStoreListener from "../../../hooks/useStoreListener";

const {
  HOVER_TYPE_NONE,
  HOVER_TYPE_CUSTOM,
  HOVER_TYPE_PAGE,
  HOVER_TYPE_BLOCK,
  ELEMENT_TYPE_ALL,
  ELEMENT_TYPE_SIMPLE,
  ELEMENT_TYPE_TEXT,
  ELEMENT_TYPE_LINE,
  ELEMENT_TYPE_ICON,
} = AppConstants;

const COLOR_ELEMENT_TYPES = [
  ELEMENT_TYPE_ALL,
  ELEMENT_TYPE_SIMPLE,
  ELEMENT_TYPE_TEXT,
  ELEMENT_TYPE_LINE,
  ELEMENT_TYPE_ICON,
];

const HoverProperties = ({
  hover,
  onPropUpdate,
  defaultType = HOVER_TYPE_NONE,
  hoverPropName = "",
  showPageDefault = true,
  showBlockDefault = true,
  elementType,
  isLocked,
}) => {
  const flyoutRef = useRef();
  const inputRef = useRef();
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);

  const _formatPropName = (name) => `${hoverPropName}${name ? `.${name}` : ""}`;

  const _onCheckChange = (propInfo) =>
    onPropUpdate({ ...propInfo, value: propInfo.value ? 1 : null });

  const _onHoverChange = (propInfo) => {
    const { value } = propInfo;
    // Remove entire tranProp if value is set to none
    onPropUpdate(value === defaultType ? { name: hoverPropName, value: null } : propInfo);
    if (value === HOVER_TYPE_CUSTOM) {
      flyoutRef.current.showPopover(inputRef.current);
    }
  };

  const hoverType = hover?.get("type", defaultType) ?? defaultType;
  const hoverDuration = hover?.get("dur", 0.5) ?? 0.5;
  const hoverOpacity = hover?.get("o", 100) ?? 100;
  const hoverColor = hover?.get("c", undefined) ?? undefined;

  const hasText = elementType === ELEMENT_TYPE_TEXT || elementType === ELEMENT_TYPE_ALL;
  const hoverFont = (hasText && hover?.get("font", null)) ?? null;
  const hoverSize = (hasText && hover?.get("size", null)) ?? null;
  const hoverLetterSpacing = (hasText && hover?.get("lS", null)) ?? null;
  const hoverUnderline = (hasText && hover?.get("ul", false)) ?? false;

  const hasBorder = elementType === ELEMENT_TYPE_SIMPLE || elementType === ELEMENT_TYPE_ALL;
  const borderColor = (hasBorder && hover?.get("bC", undefined)) ?? undefined;
  const borderWidth = (hasBorder && hover?.get("bW", 0)) ?? 0;

  const { onChange: onHoverChange } = initFormatter(_onHoverChange, _formatPropName);
  const { onChange } = initFormatter(onPropUpdate, _formatPropName);
  const { onChange: onCheckChange } = initFormatter(_onCheckChange, _formatPropName);

  // Check if flyout modal is open while design settings modal is open
  const handleDesignSettingsModalOpen = () => {
    if (flyoutRef.current) {
      setIsFlyoutOpen(flyoutRef.current.hasAttribute("open"));
    }
  };

  // If design settings modal closes and flyout is open, keep flyout open
  const handleDesignSettingsModalClose = (data) => {
    const activeTheme = data?.activeTheme;
    if (!activeTheme) {
      if (flyoutRef.current && isFlyoutOpen) {
        flyoutRef.current.showPopover(inputRef.current);
      }
      setIsFlyoutOpen(false);
    }
  };

  useStoreListener(
    [UIActions.showTheme, UIActions.toggleActiveTheme],
    [handleDesignSettingsModalOpen, handleDesignSettingsModalClose],
    [isFlyoutOpen]
  );

  return (
    <>
      <Select
        label="Type"
        disabled={isLocked}
        reserveContext
        value={hoverType}
        onSISelect={onHoverChange("type", hoverType)}
        ref={inputRef}
        shift="left"
      >
        {showBlockDefault && <SelectOption value={HOVER_TYPE_BLOCK}>Canvas Default</SelectOption>}
        {showPageDefault && <SelectOption value={HOVER_TYPE_PAGE}>Page Default</SelectOption>}
        <SelectOption value={HOVER_TYPE_NONE}>None</SelectOption>
        <SelectOption value={HOVER_TYPE_CUSTOM}>Custom</SelectOption>
        {hoverType === HOVER_TYPE_CUSTOM && (
          <Button
            slot="suffix"
            data-testid="hover-custom-flyout-button"
            variant="icon"
            iconPrefix="mix"
            onSIClick={() => flyoutRef.current.showPopover(inputRef.current)}
            disabled={isLocked}
          />
        )}
      </Select>
      <Flyout data-testid="hover-flyout" ref={flyoutRef} title="Hover">
        {hoverType === HOVER_TYPE_CUSTOM && (
          <Box pb="2">
            <Input
              type={INPUT_TYPE_NUMBER}
              min="0"
              max="100"
              disabled={isLocked}
              step="10"
              label="Opacity"
              value={hoverOpacity}
              units="%"
              onSIBeforeChange={onChange("o", hoverOpacity)}
              onSIInput={onChange("o", hoverOpacity, false)}
            />
            <Input
              type={INPUT_TYPE_NUMBER}
              min="0"
              max="60"
              disabled={isLocked}
              step=".25"
              label="Time"
              value={hoverDuration}
              units="sec"
              onSIBeforeChange={onChange("dur", hoverDuration)}
              onSIInput={onChange("dur", hoverDuration, false)}
            />
            {COLOR_ELEMENT_TYPES.includes(elementType) && (
              <ColorPicker
                disabled={isLocked}
                label="Color"
                color={hoverColor}
                propName="c"
                onSIBeforeChange={onChange("c", hoverColor)}
                onSIChange={onChange("c", hoverColor)}
                onSIInput={onChange("c", hoverColor, false)}
              />
            )}
            {hasText && (
              <>
                <FontSelectInput
                  data-testid="hover-font-select"
                  allowBrowserDefault
                  shift="left"
                  label="Font"
                  disabled={isLocked}
                  reserveContext
                  value={hoverFont}
                  onChange={onChange("font", hoverFont)}
                />
                <Input
                  type={INPUT_TYPE_NUMBER}
                  min="1"
                  max="3600"
                  step="1"
                  disabled={isLocked}
                  label="Size"
                  value={hoverSize}
                  units="px"
                  onSIBeforeChange={onChange("size", hoverSize)}
                  onSIInput={onChange("size", hoverSize, false)}
                />
                <Input
                  type={INPUT_TYPE_NUMBER}
                  max="300"
                  step="0.1"
                  precision="2"
                  disabled={isLocked}
                  label="Letter Spacing"
                  value={hoverLetterSpacing}
                  units="em"
                  onSIBeforeChange={onChange("lS", hoverLetterSpacing)}
                  onSIInput={onChange("lS", hoverLetterSpacing, false)}
                />
                <Switch
                  checked={hoverUnderline}
                  disabled={isLocked}
                  label="Underlined"
                  onSIChange={onCheckChange("ul", hoverUnderline)}
                />
              </>
            )}
            {hasBorder && (
              <>
                <Input
                  type={INPUT_TYPE_NUMBER}
                  min="0"
                  max="100"
                  step="1"
                  label="Border Size"
                  value={borderWidth}
                  disabled={isLocked}
                  units="px"
                  onSIBeforeChange={onChange("bW", borderWidth)}
                  onSIInput={onChange("bW", borderWidth, false)}
                />
                <ColorPicker
                  color={borderColor}
                  disabled={isLocked}
                  label="Border"
                  propName="bC"
                  onSIBeforeChange={onChange("bC", borderColor)}
                  onSIChange={onChange("bC", borderColor)}
                  onSIInput={onChange("bC", borderColor, false)}
                />
              </>
            )}
          </Box>
        )}
      </Flyout>
    </>
  );
};

export default HoverProperties;
